<template>
  <div class="tasks-view">
    <div class="page-header">
      <div>
        <h1 class="page-title">{{ isMine ? t('tasks.myTitle') : t('tasks.teamTitle') }}</h1>
        <p class="page-sub">{{ isMine ? t('tasks.mySub') : t('tasks.teamSub') }}</p>
      </div>
      <div class="header-actions">
        <button class="btn btn-ghost" :disabled="!tasks.length" @click="exportTasks">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
          {{ t('common.export') }}
        </button>
        <button v-if="!isExternal" class="btn btn-primary" @click="openCreateForm">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          {{ t('tasks.newTask') }}
        </button>
      </div>
    </div>

    <!-- Filters bar -->
    <div class="filters">
      <div class="status-tabs">
        <button
          v-for="s in statusOpts"
          :key="s.value"
          class="tab-btn"
          :class="{ active: filters.status === s.value }"
          @click="filters.status = s.value; load()"
        >{{ s.label }}</button>
      </div>
      <div class="filter-controls">
        <select v-model="filters.urgency" class="form-control filter-sel" @change="load()">
          <option value="">{{ t('tasks.allUrgency') }}</option>
          <option value="critical">{{ t('urgency.critical') }}</option>
          <option value="high">{{ t('urgency.high') }}</option>
          <option value="normal">{{ t('urgency.normal') }}</option>
          <option value="low">{{ t('urgency.low') }}</option>
        </select>
        <select v-model="filters.project_id" class="form-control filter-sel" @change="load()">
          <option value="">{{ t('tasks.allProjects') }}</option>
          <option v-for="p in projects" :key="p.id" :value="p.id">{{ p.name }}</option>
        </select>
        <div class="date-filter">
          <input v-model="filters.due_from" type="date" class="form-control filter-date" :title="t('common.from')" @change="load()" />
          <span class="date-sep">–</span>
          <input v-model="filters.due_to" type="date" class="form-control filter-date" :title="t('common.to')" @change="load()" />
        </div>
        <input
          v-model="filters.q"
          class="form-control filter-search"
          :placeholder="t('common.search')"
          @keyup.enter="load()"
        />
        <button class="btn btn-ghost btn-sm" @click="resetFilters">{{ t('common.reset') }}</button>
      </div>
    </div>

    <div v-if="loading" class="spinner"></div>

    <div v-else-if="tasks.length === 0" class="empty-state">
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" opacity=".3"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/></svg>
      <p>{{ t('tasks.empty') }}</p>
    </div>

    <div v-else class="tasks-grid">
      <TaskCard
        v-for="task in tasks"
        :key="task.id"
        :task="task"
        @edit="editTask"
        @complete="completeTask"
        @reopen="reopenTask"
        @archive="archiveTask"
        @timer-update="onTimerUpdate"
      />
    </div>

    <TaskFormModal
      v-if="showForm"
      :task="editingTask"
      :projects="projects"
      @close="closeForm"
      @saved="onSaved"
    />
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import TaskCard      from '@/components/TaskCard.vue'
import TaskFormModal from '@/components/TaskFormModal.vue'
import { useI18n } from '@/composables/useI18n'
import { useAuth } from '@/composables/useAuth'
import { onRealtime, debounce } from '@/composables/useRealtime'
import { exportCsv, secondsToHours } from '@/utils/exportCsv'
import api from '@/api'

export default {
  name: 'TasksView',
  components: { TaskCard, TaskFormModal },
  setup() {
    const route   = useRoute()
    const { t }   = useI18n()
    const { isExternalClient } = useAuth()
    const isExternal = computed(() => isExternalClient())
    const statusOpts = computed(() => [
      { value: '',            label: t('common.all') },
      { value: 'open',        label: t('status.open') },
      { value: 'in_progress', label: t('status.in_progress') },
      { value: 'done',        label: t('status.done') },
    ])
    const tasks    = ref([])
    const projects = ref([])
    const loading  = ref(true)
    const showForm = ref(false)
    const editingTask = ref(null)

    const isMine = computed(() => !!route.query.mine)

    const filters = reactive({ status: '', urgency: '', project_id: route.query.project_id || '', q: '', due_from: '', due_to: '' })

    async function load() {
      loading.value = true
      try {
        const params = {
          ...(isMine.value         ? { mine: 1 }                           : {}),
          ...(filters.status       ? { status: filters.status }            : {}),
          ...(filters.urgency      ? { urgency: filters.urgency }          : {}),
          ...(filters.project_id   ? { project_id: filters.project_id }    : {}),
          ...(filters.q.trim()     ? { q: filters.q.trim() }               : {}),
          ...(filters.due_from     ? { due_from: filters.due_from }        : {}),
          ...(filters.due_to       ? { due_to: filters.due_to }            : {}),
        }
        const res    = await api.getTasks(params)
        tasks.value  = res.data || []
      } catch {} finally {
        loading.value = false
      }
    }

    async function loadProjects() {
      try { projects.value = (await api.getProjects()).data || [] } catch {}
    }

    function resetFilters() {
      Object.assign(filters, { status: '', urgency: '', project_id: '', q: '', due_from: '', due_to: '' })
      load()
    }

    async function completeTask(task) {
      try { await api.completeTask(task.id); await load() } catch {}
    }
    async function reopenTask(task) {
      try { await api.reopenTask(task.id); await load() } catch {}
    }
    async function archiveTask(task) {
      if (!confirm(t('tasks.archiveConfirm', { title: task.title }))) return
      try { await api.archiveTask(task.id); tasks.value = tasks.value.filter(t => t.id !== task.id) } catch {}
    }
    function editTask(task) { editingTask.value = task; showForm.value = true }
    function openCreateForm() { editingTask.value = null; showForm.value = true }
    function closeForm() { showForm.value = false; editingTask.value = null }
    function onSaved() { load() }

    function exportTasks() {
      const rows = tasks.value.map(tk => ({
        [t('export.task')]:     tk.title,
        [t('export.project')]:  tk.project_name || '',
        [t('export.assignee')]: tk.assignee_name || '',
        [t('export.status')]:   t('status.' + tk.status),
        [t('export.urgency')]:  t('urgency.' + tk.urgency),
        [t('export.hours')]:    secondsToHours(tk.total_seconds),
        [t('export.due')]:      tk.due_at ? tk.due_at.slice(0, 10) : '',
      }))
      const stamp = new Date().toISOString().slice(0, 10)
      exportCsv(rows, `devtask-tasks-${stamp}.csv`)
    }

    function onTimerUpdate(payload) {
      const t = tasks.value.find(t => t.id === payload.taskId)
      if (t) {
        t.timer_seconds = payload.timer_seconds
        t.timer_started_at = payload.timer_started_at
        if (payload.status) t.status = payload.status
      }
    }

    // React to navigation between project/mine views (e.g. clicking "View tasks").
    watch(() => [route.query.mine, route.query.project_id], () => {
      filters.project_id = route.query.project_id || ''
      load()
    })
    let offRealtime = () => {}
    onMounted(() => {
      load(); loadProjects()
      offRealtime = onRealtime('task.changed', debounce(load, 400))
    })
    onUnmounted(() => offRealtime())

    return {
      statusOpts, tasks, projects, loading, showForm, editingTask,
      isMine, isExternal, filters, t,
      load, resetFilters, completeTask, reopenTask, archiveTask,
      editTask, openCreateForm, closeForm, onSaved, onTimerUpdate, exportTasks,
    }
  },
}
</script>

<style scoped>
.page-sub { color: var(--muted); font-size: 13px; margin-top: 2px; }
.header-actions { display: flex; gap: 8px; align-items: center; }

.filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
  padding: 14px 16px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
}
.status-tabs { display: flex; gap: 4px; }
.tab-btn {
  padding: 5px 14px;
  border-radius: 20px;
  border: 1px solid var(--border);
  background: none;
  color: var(--muted);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all .15s;
}
.tab-btn:hover  { border-color: var(--primary); color: var(--primary); }
.tab-btn.active { background: var(--primary); border-color: var(--primary); color: #fff; }

.filter-controls { display: flex; gap: 8px; flex-wrap: wrap; margin-left: auto; }
.filter-sel    { width: 140px; padding: 6px 10px; font-size: 12px; }
.filter-search { width: 180px; padding: 6px 10px; font-size: 12px; }
.date-filter   { display: flex; align-items: center; gap: 6px; }
.filter-date   { width: 140px; padding: 6px 10px; font-size: 12px; }
.date-sep      { color: var(--muted); }

.tasks-grid {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

@media (max-width: 700px) {
  .filter-controls { flex-direction: column; }
  .filter-sel, .filter-search { width: 100%; }
  .tasks-grid { grid-template-columns: 1fr; }
}
</style>
